'use client'
import { useMemo } from 'react'
import useUser from 'auth/hooks/useUser'
import type { Option, PaymentProviderConfig } from 'ecosystem'
import { getCountryCodeFromPaymentConfig } from 'api/utils/payment/getCountryCodeFromPaymentConfig'
import { type BuyerVatRates, buyerVatRatesFor, DEFAULT_COUNTRY } from 'shared-utils'

/**
 * Resolve the VAT treatment of the logged-in buyer and the price/rate
 * conversion that follows from it.
 *
 * The storefront owns this: the platform stores `vatTreatment` to *classify*
 * the customer, but deliberately leaves per-line rates to whoever builds the
 * order. Order Management only warns on a contradiction and never corrects
 * one, and the accounting push takes each line's `vatRate` verbatim — so if we
 * get it wrong here, nothing downstream fixes it.
 *
 * Applies identically to all three flows (UPFRONT / INVOICE / PROFORMA): VAT
 * follows the buyer, never the payment type.
 */
export const useBuyerVat = (
  paymentProviderConfig?: Option<PaymentProviderConfig>
): BuyerVatRates => {
  const { user } = useUser()
  const companyProfile = user?.customer?.companyProfile

  // The tenant's own jurisdiction. The payment provider's configured country is
  // the closest thing the storefront has to "where this store sells from".
  const tenantCountry = getCountryCodeFromPaymentConfig(paymentProviderConfig) ?? DEFAULT_COUNTRY

  return useMemo(
    () => buyerVatRatesFor(companyProfile, tenantCountry),
    [companyProfile, tenantCountry]
  )
}
